<template>
    <div id="app">

        <!-- Row Detail Popup -->
        <div v-if="selectedRecord" class="simple-popup" @click.self="selectedRecord = null">
            <div class="popup-content wide-popup">
                <div class="popup-header">
                    <h3>📋 Logbook Record Detail</h3>
                    <div class="popup-actions">
                        <button @click="exportRecordToPDF" class="export-pdf-btn" title="Export to PDF">
                            <span>📥</span> Export PDF
                        </button>
                        <button @click="selectedRecord = null" class="close-btn">×</button>
                    </div>
                </div>
                <div class="popup-body">
                    <div class="detail-sections">
                        <div v-for="(chunk, ci) in detailChunks" :key="ci" class="detail-section">
                            <div class="detail-grid">
                                <div v-for="col in chunk" :key="col" class="detail-row">
                                    <strong>{{ formatHeader(col) }}:</strong>
                                    {{ formatCell(selectedRecord[col], col) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Header -->
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon"><span>📒</span></div>
                    <h1 class="dashboard-title">Logbook Report</h1>
                </div>
                <div class="header-controls">
                    <div class="live-indicator">
                        <div class="live-dot"></div>
                        <span style="color:white;font-size:0.875rem;">Compliant</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">

            <!-- Summary Stats -->
            <div class="stats-section">
                <div class="stats-header">
                    <h3 class="stats-title">Logbook Overview</h3>
                    <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
                        <span>{{ statsVisible ? '📊' : '📈' }}</span>
                        {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
                    </button>
                </div>
                <transition name="slide-fade">
                    <div v-if="statsVisible" class="stats-wrapper">
                        <div class="stats-container">
                            <div class="stat-card total-stat">
                                <div class="stat-label">Total Records</div>
                                <div class="stat-value">{{ records.length }}</div>
                                <div class="stat-change positive"><span>📋</span> All logbook entries</div>
                            </div>
                            <div class="stat-card volume-stat">
                                <div class="stat-label">Filtered Records</div>
                                <div class="stat-value" style="color:#4CAF50;">{{ filteredRecords.length }}</div>
                                <div class="stat-change"><span>🔎</span> After filters</div>
                            </div>
                            <div class="stat-card cost-stat" v-if="totalBusinessKm !== null">
                                <div class="stat-label">Business km</div>
                                <div class="stat-value" style="color:#27AE60;">{{ formatKm(totalBusinessKm) }}</div>
                                <div class="stat-change positive"><span>🚗</span> Deductible km</div>
                            </div>
                            <div class="stat-card equipment-stat" v-if="totalPrivateKm !== null">
                                <div class="stat-label">Private km</div>
                                <div class="stat-value" style="color:#2196F3;">{{ formatKm(totalPrivateKm) }}</div>
                                <div class="stat-change"><span>🏠</span> Non-deductible km</div>
                            </div>
                            <div class="stat-card product-stat" v-if="totalKm !== null">
                                <div class="stat-label">Total km</div>
                                <div class="stat-value" style="color:#9C27B0;">{{ formatKm(totalKm) }}</div>
                                <div class="stat-change"><span>📍</span> All distance</div>
                            </div>
                            <div class="stat-card average-stat" v-if="dateRange">
                                <div class="stat-label">Date Range</div>
                                <div class="stat-value" style="font-size:1.1rem;">{{ dateRange }}</div>
                                <div class="stat-change"><span>📅</span> Period covered</div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Filters Section -->
            <div class="search-section" :class="{ collapsed: !filtersVisible }">
                <div class="filter-header">
                    <h3 class="filter-title">Data Filters &amp; Reports</h3>
                    <div class="filter-header-buttons">
                        <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
                            <span>{{ filtersVisible ? '📁' : '📂' }}</span>
                            {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
                        </button>
                        <button class="reset-filter-header-btn" @click="resetFilters">
                            <span class="reset-icon">↻</span> Reset Filters
                        </button>
                    </div>
                </div>
                <transition name="slide-fade">
                    <div v-if="filtersVisible" class="search-controls-wrapper">
                        <div class="search-controls">
                            <div class="search-group date-filter-group">
                              <label class="search-label">Date Range <span class="date-range-showing">{{ activeDateRangeLabel }}</span></label>
                              <div class="date-quick-buttons">
                                <button v-for="opt in dateRangeOptions" :key="opt.value"
                                  :class="['date-quick-btn', { active: selectedDateRange === opt.value }]"
                                  @click="selectDateRange(opt.value)">{{ opt.label }}</button>
                              </div>
                            </div>

                            <!-- Specific Date (within last 7 days) -->
                            <div class="search-group">
                                <label class="search-label">
                                    Specific Date
                                    <span class="date-range-showing">(within last 7 days)</span>
                                </label>
                                <input
                                    type="date"
                                    v-model="filters.specificDate"
                                    :min="minAllowedDate"
                                    :max="maxAllowedDate"
                                    @change="onSpecificDateChange"
                                    class="specific-date-input"
                                    title="Pick any single day within the last 7 days"
                                />
                            </div>

                            <div class="search-group">
                                <label class="search-label">Quick Search</label>
                                <input type="text" v-model="quickSearch" @input="applyFilters" class="search-input"
                                    placeholder="Search any field…" />
                            </div>

                            <!-- Operation Filter -->
                            <div class="search-group" v-if="uniqueOperations.length > 0">
                                <label class="search-label">
                                    Operation
                                    <span class="multi-badge orange-badge" v-if="selectedFilters.operation.length > 0">({{ selectedFilters.operation.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('operation')">
                                        <span v-if="selectedFilters.operation.length === 0" class="selected-items">All operations</span>
                                        <span v-else-if="selectedFilters.operation.length === uniqueOperations.length" class="selected-items">All operations</span>
                                        <span v-else class="selected-items">{{ selectedFilters.operation.length }} selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.operation" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll('operation')"
                                                    :checked="selectedFilters.operation.length === uniqueOperations.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="op in uniqueOperations" :key="op">
                                                <input type="checkbox" :value="op" v-model="selectedFilters.operation" @change="applyFilters" />
                                                <span>{{ op || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        
                            <!-- Location Filter -->
                            <div class="search-group" v-if="uniqueLocations.length > 0">
                                <label class="search-label">
                                    Location
                                    <span class="multi-badge orange-badge" v-if="selectedFilters.location.length > 0">({{ selectedFilters.location.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('location')">
                                        <span v-if="selectedFilters.location.length === 0" class="selected-items">All locations</span>
                                        <span v-else-if="selectedFilters.location.length === uniqueLocations.length" class="selected-items">All locations</span>
                                        <span v-else class="selected-items">{{ selectedFilters.location.length }} selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.location" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll('location')"
                                                    :checked="selectedFilters.location.length === uniqueLocations.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="loc in uniqueLocations" :key="loc">
                                                <input type="checkbox" :value="loc" v-model="selectedFilters.location" @change="applyFilters" />
                                                <span>{{ loc || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Registration Filter -->
                            <div class="search-group" v-if="uniqueRegistrations.length > 0">
                                <label class="search-label">
                                    Registration
                                    <span class="multi-badge orange-badge" v-if="selectedFilters.registration.length > 0">({{ selectedFilters.registration.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('registration')">
                                        <span v-if="selectedFilters.registration.length === 0" class="selected-items">All registrations</span>
                                        <span v-else-if="selectedFilters.registration.length === uniqueRegistrations.length" class="selected-items">All registrations</span>
                                        <span v-else class="selected-items">{{ selectedFilters.registration.length }} selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.registration" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll('registration')"
                                                    :checked="selectedFilters.registration.length === uniqueRegistrations.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="reg in uniqueRegistrations" :key="reg">
                                                <input type="checkbox" :value="reg" v-model="selectedFilters.registration" @change="applyFilters" />
                                                <span>{{ reg || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Fams Source Filter -->
                            <div class="search-group" v-if="uniqueFamsSources.length > 0">
                                <label class="search-label">
                                    Fams Source
                                    <span class="multi-badge orange-badge" v-if="selectedFilters.famsSource.length > 0">({{ selectedFilters.famsSource.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('famsSource')">
                                        <span v-if="selectedFilters.famsSource.length === 0" class="selected-items">All sources</span>
                                        <span v-else-if="selectedFilters.famsSource.length === uniqueFamsSources.length" class="selected-items">All sources</span>
                                        <span v-else class="selected-items">{{ selectedFilters.famsSource.length }} selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.famsSource" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll('famsSource')"
                                                    :checked="selectedFilters.famsSource.length === uniqueFamsSources.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="src in uniqueFamsSources" :key="src">
                                                <input type="checkbox" :value="src" v-model="selectedFilters.famsSource" @change="applyFilters" />
                                                <span>{{ src || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Record Type Filter -->
                            <div class="search-group" v-if="uniqueRecordTypes.length > 0">
                                <label class="search-label">
                                    Record Type
                                    <span class="multi-badge orange-badge" v-if="selectedFilters.recordType.length > 0">({{ selectedFilters.recordType.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('recordType')">
                                        <span v-if="selectedFilters.recordType.length === 0" class="selected-items">All types</span>
                                        <span v-else-if="selectedFilters.recordType.length === uniqueRecordTypes.length" class="selected-items">All types</span>
                                        <span v-else class="selected-items">{{ selectedFilters.recordType.length }} selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.recordType" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll('recordType')"
                                                    :checked="selectedFilters.recordType.length === uniqueRecordTypes.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="type in uniqueRecordTypes" :key="type">
                                                <input type="checkbox" :value="type" v-model="selectedFilters.recordType" @change="applyFilters" />
                                                <span>{{ type || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Cost Centre Filter -->
                            <div class="search-group" v-if="uniqueCostcentres.length > 0">
                                <label class="search-label">
                                    Cost Centre
                                    <span class="multi-badge orange-badge" v-if="selectedFilters.costcentre.length > 0">({{ selectedFilters.costcentre.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display" @click="toggleDropdown('costcentre')">
                                        <span v-if="selectedFilters.costcentre.length === 0" class="selected-items">All cost centres</span>
                                        <span v-else-if="selectedFilters.costcentre.length === uniqueCostcentres.length" class="selected-items">All cost centres</span>
                                        <span v-else class="selected-items">{{ selectedFilters.costcentre.length }} selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.costcentre" class="multi-select-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleFilterAll('costcentre')"
                                                    :checked="selectedFilters.costcentre.length === uniqueCostcentres.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="cc in uniqueCostcentres" :key="cc">
                                                <input type="checkbox" :value="cc" v-model="selectedFilters.costcentre" @change="applyFilters" />
                                                <span>{{ cc || '(empty)' }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Visible Columns -->
                            <div class="search-group" v-if="columns.length > 0">
                                <label class="search-label">
                                    Visible Columns
                                    <span class="multi-badge orange-badge">({{ visibleColumns.length }}/{{
                                        columns.length }})</span>
                                </label>
                                <div class="multi-select-container">
                                    <div class="multi-select-display columns-select" @click="toggleDropdown('columns')">
                                        <span v-if="visibleColumns.length === columns.length" class="selected-items">All
                                            columns selected</span>
                                        <span v-else class="selected-items">{{ visibleColumns.length }} columns
                                            selected</span>
                                        <span class="dropdown-arrow">▼</span>
                                    </div>
                                    <div v-if="dropdownStates.columns" class="multi-select-dropdown columns-dropdown">
                                        <div class="select-all-option">
                                            <label class="checkbox-option">
                                                <input type="checkbox" @change="toggleAllColumns"
                                                    :checked="visibleColumns.length === columns.length" />
                                                <span>Select All</span>
                                            </label>
                                        </div>
                                        <div class="dropdown-options">
                                            <label class="checkbox-option" v-for="col in columns" :key="col">
                                                <input type="checkbox" :value="col" v-model="visibleColumns"
                                                    @change="onColumnToggle" />
                                                <span>{{ formatHeader(col) }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div class="filter-actions">
                            <button class="apply-filters-btn" @click="loadData">🔍 Load Report</button>
                            <button class="reset-filters-btn" @click="resetFilters">↺ Reset</button>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Data Grid -->
            <div class="grid-container">
                <div class="grid-header">
                    <div class="grid-title">
                        <span class="report-name">
                            📒 Logbook Entries
                            <span v-if="!loading && records.length > 0" class="record-count-badge">
                                {{ filteredRecords.length }} records
                            </span>
                        </span>
                        <span class="date-range-label" v-if="dateRange">Date: {{ dateRange }}</span>
                    </div>
                    <div class="grid-controls">
                        <button v-if="hasActiveColumnSearches" class="clear-column-searches-btn"
                            @click="clearColumnSearches">
                            <span>✖</span><span>Clear Searches</span>
                        </button>
                        <div class="rows-selector">
                            <span class="rows-label">Show:</span>
                            <select v-model.number="itemsPerPage" @change="currentPage = 1" class="rows-select">
                                <option :value="10">10 rows</option>
                                <option :value="25">25 rows</option>
                                <option :value="50">50 rows</option>
                                <option :value="100">100 rows</option>
                                <option :value="200">200 rows</option>
                            </select>
                        </div>
                        <div class="grid-actions">
                            <button class="action-button popout-btn" @click="printReport">
                                <span>🖨️</span><span>Print</span>
                            </button>
                            <button class="action-button refresh-btn" @click="loadData">
                                <span>🔄</span><span>Refresh</span>
                            </button>
                            <button class="action-button export-btn" @click="exportCSV">
                                <span>📥</span><span>Export</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="table-container">
                    <div v-if="loading" class="loading-overlay">
                        <div class="loader"></div>
                    </div>

                    <div v-if="!loading && records.length === 0" class="empty-state">
                        <div class="empty-icon">📭</div>
                        <div class="empty-title">No Data Loaded</div>
                        <div class="empty-description">Select a date range and click <strong>Load Report</strong>.</div>
                    </div>

                    <table v-if="!loading && records.length > 0" class="data-table">
                        <thead>
                            <tr>
                                <th v-for="col in visibleColumns" :key="col" @click="sortBy(col)" class="sortable-th">
                                    {{ formatHeader(col) }}
                                    <span class="sort-indicator">
                                        {{ sort.col === col ? (sort.asc ? '▲' : '▼') : '⇅' }}
                                    </span>
                                </th>
                            </tr>
                            <tr class="search-row">
                                <th v-for="col in visibleColumns" :key="'s-' + col" class="search-cell">
                                    <input type="text" class="column-search-input" placeholder="Search..."
                                        v-model="colSearch[col]" @input="handleColumnSearch" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="paginatedRecords.length === 0">
                                <td :colspan="visibleColumns.length" class="empty-state"
                                    style="padding:40px;text-align:center;">
                                    <div>No matching records — try clearing the filters.</div>
                                </td>
                            </tr>
                            <tr v-for="(rec, idx) in paginatedRecords" :key="idx" :class="getRowClass(rec)"
                                @click="selectedRecord = rec">
                                <td v-for="col in visibleColumns" :key="col">
                                    <div v-if="isKmCol(col)" class="volume-cell-new">
                                        <span class="volume-value">{{ formatKmNum(rec[col]) }}</span>
                                        <span class="volume-unit" v-if="rec[col] != null && rec[col] !== ''">km</span>
                                    </div>
                                    <code v-else-if="isIdCol(col)"
                                        class="id-code">{{ rec[col] != null ? rec[col] : '—' }}</code>
                                    <div v-else-if="isDateCol(col)" class="date-time-cell">
                                        <div class="date-part">{{ formatCell(rec[col], col) }}</div>
                                    </div>
                                    <span v-else>{{ formatCell(rec[col], col) }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="totalPages > 1" class="pagination-container">
                    <div class="pagination-info">
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                        {{ Math.min(currentPage * itemsPerPage, filteredRecords.length) }}
                        of {{ filteredRecords.length }} entries
                    </div>
                    <div class="pagination-controls">
                        <button class="page-btn" @click="currentPage = 1" :disabled="currentPage === 1">First</button>
                        <button class="page-btn" @click="currentPage--" :disabled="currentPage === 1">Previous</button>
                        <button v-for="p in visiblePageNumbers" :key="p" class="page-btn page-number"
                            :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</button>
                        <button class="page-btn" @click="currentPage++"
                            :disabled="currentPage === totalPages">Next</button>
                        <button class="page-btn" @click="currentPage = totalPages"
                            :disabled="currentPage === totalPages">Last</button>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    name: 'IOTReport',

    data: function () {
        return {
            loading: false,
            records: [],
            columns: [],
            visibleColumns: [],
            params: { fromDate: '', toDate: '' },
            filters: { specificDate: '', startDateTime: '', endDateTime: '' },
            colSearch: {},
            quickSearch: '',
            selectedDateRange: 'last3days',
            dateRangeOptions: [
              { value: 'last3days', label: 'Last 3 Days' },
              { value: 'today',     label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: '2daysago',  label: '2 Days Ago' }
            ],
            filtersVisible: true,
            statsVisible: true,
            dropdownStates: { columns: false, operation: false, location: false, registration: false, famsSource: false, recordType: false, costcentre: false },
            _handleClickOutsideBound: null,
            currentPage: 1,
            itemsPerPage: 25,
            sort: { col: null, asc: true },
            selectedRecord: null,
            selectedFilters: { operation: [], location: [], registration: [], famsSource: [], recordType: [], costcentre: [] },
            uniqueOperations: [],
            uniqueLocations: [],
            uniqueRegistrations: [],
            uniqueFamsSources: [],
            uniqueRecordTypes: [],
            uniqueCostcentres: []
        };
    },

    mounted: function () {
        var today = new Date();
        var fmt = function (d) {
            return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
        };
        // Default to last 3 days (backend cap). A specific date within 7 days can be chosen via the date picker.
        var threeDaysAgo = new Date(today);
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        this.params.fromDate = fmt(threeDaysAgo);
        this.params.toDate = fmt(today);
        var self = this;
        this._handleClickOutsideBound = function (e) { self.handleClickOutside(e); };
        document.addEventListener('click', this._handleClickOutsideBound);
        this.loadData();
    },

    beforeDestroy: function () {
        if (this._handleClickOutsideBound) document.removeEventListener('click', this._handleClickOutsideBound);
    },

    computed: {
        hasActiveColumnSearches: function () {
            var self = this;
            return this.columns.some(function (c) { return self.colSearch[c]; });
        },

        minAllowedDate: function() {
          // Allow picking any date within the last 7 days (backend max window)
          var d = new Date();
          d.setDate(d.getDate() - 7);
          return d.toISOString().split('T')[0];
        },

        maxAllowedDate: function() {
          return new Date().toISOString().split('T')[0];
        },

        activeDateRangeLabel: function() {
          if (this.filters.specificDate)
            return 'Showing: ' + this.filters.specificDate + ' (00:00 – 23:59)';
          var opt = this.dateRangeOptions.find(function(o) { return o.value === this.selectedDateRange; }.bind(this));
          return opt ? opt.label : '';
        },

        filteredRecords: function () {
            var self = this;
            var result = this.records.slice();
            if (this.quickSearch) {
                var qs = this.quickSearch.toLowerCase();
                result = result.filter(function (r) {
                    return self.columns.some(function (c) { var v = r[c]; return v != null && String(v).toLowerCase().includes(qs); });
                });
            }
            this.columns.forEach(function (col) {
                var term = (self.colSearch[col] || '').toLowerCase();
                if (!term) return;
                result = result.filter(function (r) { var v = r[col]; return v != null && String(v).toLowerCase().includes(term); });
            });
            
            // Apply filter selections
            var operationCol = this.findCol(['Operation', 'operation', 'OperationType', 'operationType']);
            if (operationCol && this.selectedFilters.operation.length > 0) {
                result = result.filter(function (r) { return self.selectedFilters.operation.includes(r[operationCol]); });
            }
            
            var locationCol = this.findCol(['Location', 'location', 'LocationName', 'locationName']);
            if (locationCol && this.selectedFilters.location.length > 0) {
                result = result.filter(function (r) { return self.selectedFilters.location.includes(r[locationCol]); });
            }
            
            var registrationCol = this.findCol(['Registration', 'registration', 'Reg', 'reg', 'VehicleReg', 'vehicleReg']);
            if (registrationCol && this.selectedFilters.registration.length > 0) {
                result = result.filter(function (r) { return self.selectedFilters.registration.includes(r[registrationCol]); });
            }
            
            var famsSourceCol = this.findCol(['FamsSource', 'famsSource', 'FAMS_Source', 'FamSource', 'Source', 'source']);
            if (famsSourceCol && this.selectedFilters.famsSource.length > 0) {
                result = result.filter(function (r) { return self.selectedFilters.famsSource.includes(r[famsSourceCol]); });
            }
            
            var recordTypeCol = this.findCol(['RecordType', 'recordType', 'Type', 'type', 'EntryType', 'entryType']);
            if (recordTypeCol && this.selectedFilters.recordType.length > 0) {
                result = result.filter(function (r) { return self.selectedFilters.recordType.includes(r[recordTypeCol]); });
            }
            
            var costcentreCol = this.findCol(['CostCentre', 'costcentre', 'CostCenter', 'costcenter', 'Cost_Centre', 'CostCentre_Code', 'CC', 'cc']);
            if (costcentreCol && this.selectedFilters.costcentre.length > 0) {
                result = result.filter(function (r) { return self.selectedFilters.costcentre.includes(r[costcentreCol]); });
            }
            
            if (this.sort.col) {
                var sc = this.sort.col, asc = this.sort.asc;
                result = result.slice().sort(function (a, b) {
                    var av = a[sc] != null ? a[sc] : '', bv = b[sc] != null ? b[sc] : '';
                    var na = parseFloat(av), nb = parseFloat(bv);
                    if (!isNaN(na) && !isNaN(nb)) return asc ? na - nb : nb - na;
                    return asc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
                });
            }
            return result;
        },

        totalPages: function () { return Math.ceil(this.filteredRecords.length / this.itemsPerPage); },

        paginatedRecords: function () {
            var s = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredRecords.slice(s, s + this.itemsPerPage);
        },

        visiblePageNumbers: function () {
            var maxV = 5, pages = [], start = Math.max(1, this.currentPage - 2);
            var end = Math.min(this.totalPages, start + maxV - 1);
            if (end - start < maxV - 1) start = Math.max(1, end - maxV + 1);
            for (var i = start; i <= end; i++) pages.push(i);
            return pages;
        },

        totalBusinessKm: function () { return this.sumCol(['BusinessKM', 'BusinessKm', 'business_km', 'BusKm', 'BusinessDistance']); },
        totalPrivateKm: function () { return this.sumCol(['PrivateKM', 'PrivateKm', 'private_km', 'PrivKm', 'PrivateDistance']); },
        totalKm: function () { return this.sumCol(['TotalKM', 'TotalKm', 'total_km', 'Distance', 'TotalDistance', 'Km', 'KM']); },

        dateRange: function () {
            if (!this.params.fromDate && !this.params.toDate) return null;
            return (this.params.fromDate || '—') + ' → ' + (this.params.toDate || '—');
        },

        detailChunks: function () {
            if (!this.selectedRecord) return [];
            var cols = this.columns, cs = Math.ceil(cols.length / 2), result = [];
            for (var i = 0; i < cols.length; i += cs) result.push(cols.slice(i, i + cs));
            return result;
        }
    },

    methods: {
        loadData: function () {
            var self = this;
            if (!this.params.fromDate || !this.params.toDate) {
                alert('Please select both From Date and To Date.');
                return;
            }
            self.loading = true; self.records = []; self.columns = []; self.visibleColumns = []; self.colSearch = {}; self.currentPage = 1;
            var url = 'IOTReportsSARS?fromDate=' + encodeURIComponent(self.params.fromDate) + '&toDate=' + encodeURIComponent(self.params.toDate);
            var onSuccess = function (response) {
                var data = response.data;
                if (!data || !data.length) return;
                self.columns = Object.keys(data[0]);
                self.visibleColumns = self.columns.slice();
                self.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });
                self.records = data;
                self.extractUniqueFilterValues();
            };
            self.$ajaxGet(self, url, onSuccess, function () { self.loading = false; });
        },

        extractUniqueFilterValues: function () {
            var self = this;
            var operationCol = this.findCol(['Operation', 'operation', 'OperationType', 'operationType']);
            var locationCol = this.findCol(['Location', 'location', 'LocationName', 'locationName']);
            var registrationCol = this.findCol(['Registration', 'registration', 'Reg', 'reg', 'VehicleReg', 'vehicleReg']);
            var famsSourceCol = this.findCol(['FamsSource', 'famsSource', 'FAMS_Source', 'FamSource', 'Source', 'source']);
            var recordTypeCol = this.findCol(['RecordType', 'recordType', 'Type', 'type', 'EntryType', 'entryType']);
            var costcentreCol = this.findCol(['CostCentre', 'costcentre', 'CostCenter', 'costcenter', 'Cost_Centre', 'CostCentre_Code', 'CC', 'cc']);
            
            this.uniqueOperations = this.getUniqueValues(operationCol);
            this.uniqueLocations = this.getUniqueValues(locationCol);
            this.uniqueRegistrations = this.getUniqueValues(registrationCol);
            this.uniqueFamsSources = this.getUniqueValues(famsSourceCol);
            this.uniqueRecordTypes = this.getUniqueValues(recordTypeCol);
            this.uniqueCostcentres = this.getUniqueValues(costcentreCol);
            
            this.selectedFilters = { operation: [], location: [], registration: [], famsSource: [], recordType: [], costcentre: [] };
        },

        getUniqueValues: function (col) {
            if (!col) return [];
            var unique = [];
            this.records.forEach(function (r) { var v = r[col]; if (v != null && v !== '' && !unique.includes(v)) unique.push(v); });
            return unique.sort();
        },

        applyFilters: function () { this.currentPage = 1; },
        handleColumnSearch: function () { this.currentPage = 1; },

        clearColumnSearches: function () {
            var self = this;
            this.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });
            this.currentPage = 1;
        },

        resetFilters: function () {
            // Restore default 3-day window
            var now = new Date();
            var fmt = function(d) {
              return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
            };
            var threeDaysAgo = new Date(now);
            threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
            this.params.fromDate = fmt(threeDaysAgo);
            this.params.toDate   = fmt(now);
            this.quickSearch = '';
            this.filters = { specificDate: '', startDateTime: '', endDateTime: '' };
            this.selectedDateRange = 'last3days';
            var self = this;
            this.columns.forEach(function (c) { self.$set(self.colSearch, c, ''); });
            this.selectedFilters = { operation: [], location: [], registration: [], famsSource: [], recordType: [], costcentre: [] };
            this.currentPage = 1; this.sort = { col: null, asc: true };
            this.visibleColumns = this.columns.slice();
        },

        formatDateForInput: function(date) {
          var y = date.getFullYear();
          var m = String(date.getMonth() + 1).padStart(2, '0');
          var d = String(date.getDate()).padStart(2, '0');
          var h = String(date.getHours()).padStart(2, '0');
          var min = String(date.getMinutes()).padStart(2, '0');
          var s = String(date.getSeconds()).padStart(2, '0');
          return y + '-' + m + '-' + d + 'T' + h + ':' + min + ':' + s;
        },

        selectDateRange: function(rangeValue) {
          this.selectedDateRange = rangeValue;
          this.filters.specificDate = '';
          var now = new Date();
          var fmt = function(d) {
            return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
          };

          var start = new Date(now);
          var end   = new Date(now);

          if (rangeValue === 'last3days') {
            start.setDate(start.getDate() - 3);
            // end stays as today
          } else if (rangeValue === 'yesterday') {
            start.setDate(start.getDate() - 1);
            end.setDate(end.getDate() - 1);
          } else if (rangeValue === '2daysago') {
            start.setDate(start.getDate() - 2);
            end.setDate(end.getDate() - 2);
          }
          // 'today': start and end both remain today

          this.params.fromDate = fmt(start);
          this.params.toDate   = fmt(end);
          this.currentPage = 1;
        },

        onSpecificDateChange: function() {
          if (!this.filters.specificDate) return;
          // Deselect any quick-range button
          this.selectedDateRange = '';
          // Send just that single day to the API
          this.params.fromDate = this.filters.specificDate;
          this.params.toDate   = this.filters.specificDate;
          this.currentPage = 1;
        },

        toggleDropdown: function (key) {
            var self = this;
            Object.keys(this.dropdownStates).forEach(function (k) { self.dropdownStates[k] = k === key ? !self.dropdownStates[k] : false; });
        },

        toggleFilterAll: function (filterKey) {
            var filterArray = this.selectedFilters[filterKey];
            var uniqueArray = this['unique' + filterKey.charAt(0).toUpperCase() + filterKey.slice(1) + (filterKey === 'famsSource' ? '' : 's')];
            
            if (filterKey === 'operation') uniqueArray = this.uniqueOperations;
            else if (filterKey === 'location') uniqueArray = this.uniqueLocations;
            else if (filterKey === 'registration') uniqueArray = this.uniqueRegistrations;
            else if (filterKey === 'famsSource') uniqueArray = this.uniqueFamsSources;
            else if (filterKey === 'recordType') uniqueArray = this.uniqueRecordTypes;
            else if (filterKey === 'costcentre') uniqueArray = this.uniqueCostcentres;
            
            if (filterArray.length === uniqueArray.length) {
                this.selectedFilters[filterKey] = [];
            } else {
                this.selectedFilters[filterKey] = uniqueArray.slice();
            }
            this.applyFilters();
        },

        handleClickOutside: function (e) {
            if (!this.$el) return;
            var inside = false;
            this.$el.querySelectorAll('.multi-select-container').forEach(function (c) { if (c.contains(e.target)) inside = true; });
            if (!inside) { var self = this; Object.keys(this.dropdownStates).forEach(function (k) { self.dropdownStates[k] = false; }); }
        },

        toggleAllColumns: function () {
            this.visibleColumns = this.visibleColumns.length === this.columns.length
                ? (this.columns.length > 1 ? [this.columns[0]] : this.columns.slice())
                : this.columns.slice();
            this.currentPage = 1;
        },

        onColumnToggle: function () {
            if (!this.visibleColumns.length) this.visibleColumns = [this.columns[0]];
            this.currentPage = 1;
        },

        sortBy: function (col) {
            if (this.sort.col === col) this.sort.asc = !this.sort.asc;
            else { this.sort.col = col; this.sort.asc = true; }
            this.currentPage = 1;
        },

        formatHeader: function (col) {
            return col.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/\bKM\b/gi, 'km').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        },

        formatCell: function (value, col) {
            if (value == null || value === '') return '—';
            if (this.isDateCol(col) || this.looksLikeDate(value)) {
                var d = new Date(value);
                if (!isNaN(d.getTime())) {
                    var pad = function (n) { return String(n).padStart(2, '0'); };
                    var base = d.getFullYear() + '/' + pad(d.getMonth() + 1) + '/' + pad(d.getDate());
                    if (String(value).includes('T') || String(value).includes(' '))
                        return base + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
                    return base;
                }
            }
            return value;
        },

        formatKm: function (v) { if (v == null) return '—'; var n = parseFloat(v); return isNaN(n) ? v : n.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' km'; },
        formatKmNum: function (v) { if (v == null || v === '') return '—'; var n = parseFloat(v); return isNaN(n) ? v : n.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }); },

        isDateCol: function (col) { return ['date', 'time', 'datetime', 'departure', 'arrival', 'start', 'end', 'trip'].some(function (k) { return col.toLowerCase().includes(k); }); },
        isKmCol: function (col) { var lc = col.toLowerCase(); return lc.includes('km') || lc.includes('distance') || lc.includes('odometer'); },
        isIdCol: function (col) { var lc = col.toLowerCase(); return lc === 'id' || lc.endsWith('id') || lc.includes('imei') || lc.includes('reg'); },
        looksLikeDate: function (v) { return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v); },

        getRowClass: function (rec) {
            var pC = this.findCol(['PrivateKM', 'PrivateKm', 'private_km', 'PrivKm']);
            var bC = this.findCol(['BusinessKM', 'BusinessKm', 'business_km', 'BusKm']);
            if (pC && bC) {
                var p = parseFloat(rec[pC]) || 0, b = parseFloat(rec[bC]) || 0;
                if (p > 0 && b === 0) return 'row-failed';
                if (b > 0 && p === 0) return 'row-success';
            }
            return '';
        },

        sumCol: function (candidates) {
            var col = this.findCol(candidates); if (!col) return null;
            var total = 0, found = false;
            this.filteredRecords.forEach(function (r) { var v = parseFloat(r[col]); if (!isNaN(v)) { total += v; found = true; } });
            return found ? total : null;
        },

        findCol: function (candidates) {
            for (var i = 0; i < candidates.length; i++) if (this.columns.includes(candidates[i])) return candidates[i];
            return null;
        },

        exportCSV: function () {
            if (!this.filteredRecords.length) { alert('No data to export.'); return; }
            var cols = this.visibleColumns;
            var rows = [cols.map(function (c) { return '"' + c + '"'; }).join(',')];
            this.filteredRecords.forEach(function (r) {
                rows.push(cols.map(function (c) { var v = r[c] != null ? r[c] : ''; return '"' + String(v).replace(/"/g, '""') + '"'; }).join(','));
            });
            var a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' }));
            a.download = 'Logbook_' + (this.params.fromDate || 'report') + '_to_' + (this.params.toDate || '') + '.csv';
            a.click();
        },

        printReport: function () { window.print(); },

        exportRecordToPDF: function () {
            var self = this;
            if (!this.selectedRecord) return;

            // Create HTML content for PDF
            var htmlContent = '<html><head><meta charset="UTF-8"><title>Logbook Record</title>';
            htmlContent += '<style>';
            htmlContent += 'body { font-family: Arial, sans-serif; margin: 20px; color: #333; }';
            htmlContent += 'h1 { color: #D2691E; border-bottom: 2px solid #D2691E; padding-bottom: 10px; margin-bottom: 20px; }';
            htmlContent += 'p { margin: 0 0 20px 0; color: #666; font-size: 12px; }';
            htmlContent += 'table { width: 100%; border-collapse: collapse; }';
            htmlContent += 'tr { page-break-inside: avoid; }';
            htmlContent += 'td { padding: 12px; border-bottom: 1px solid #ddd; }';
            htmlContent += 'td:first-child { font-weight: bold; background-color: #f8f8f8; width: 35%; }';
            htmlContent += '@media print { body { margin: 0; padding: 10mm; } }';
            htmlContent += '</style></head><body>';
            
            // Add title
            htmlContent += '<h1>Logbook Record Detail</h1>';
            
            // Add timestamp
            htmlContent += '<p>Generated on: ' + new Date().toLocaleString('en-ZA') + '</p>';
            
            // Add table
            htmlContent += '<table>';
            this.columns.forEach(function (col) {
                var fieldName = self.formatHeader(col);
                var fieldValue = self.formatCell(self.selectedRecord[col], col);
                htmlContent += '<tr><td>' + self.escapeHtml(fieldName) + '</td>';
                htmlContent += '<td>' + self.escapeHtml(fieldValue) + '</td></tr>';
            });
            htmlContent += '</table>';
            htmlContent += '</body></html>';

            // Open in new window and trigger print to PDF
            var printWindow = window.open('', '', 'width=800,height=600');
            printWindow.document.write(htmlContent);
            printWindow.document.close();
            
            // Trigger print dialog after content loads
            printWindow.onload = function () {
                printWindow.print();
            };
            
            // Fallback for some browsers
            setTimeout(function () {
                if (!printWindow.closed) {
                    printWindow.print();
                }
            }, 250);
        },

        escapeHtml: function (text) {
            var map = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#039;'
            };
            return String(text).replace(/[&<>"']/g, function (m) { return map[m]; });
        }
    }
};
</script>

<style scoped>
* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

#app {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #e2e8f0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

/* Header */
.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
    flex-shrink: 0;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.logo-icon {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
}

.dashboard-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 600;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, .2);
    padding: 8px 16px;
    border-radius: 20px;
}

.live-dot {
    width: 8px;
    height: 8px;
    background: #4CAF50;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, .7);
    }

    70% {
        box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
    }
}

/* Main */
.main-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #e2e8f0;
}

/* Stats */
.stats-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.stats-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.stats-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.toggle-stats-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.toggle-stats-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: translateY(-1px);
}

.stats-wrapper {
    padding: 16px 20px;
}

.stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.stat-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    transition: all .3s;
    border: 1px solid #e2e8f0;
}

.stat-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, .1);
    background: #fff;
}

.total-stat {
    border-left: 4px solid #FF8C00;
}

.volume-stat {
    border-left: 4px solid #4CAF50;
}

.cost-stat {
    border-left: 4px solid #27AE60;
}

.equipment-stat {
    border-left: 4px solid #2196F3;
}

.product-stat {
    border-left: 4px solid #9C27B0;
}

.average-stat {
    border-left: 4px solid #E67E22;
}

.stat-label {
    color: #2c3e50;
    font-size: .875rem;
    margin-bottom: 8px;
    font-weight: 600;
}

.stat-value {
    font-size: 1.75rem;
    color: #2c3e50;
    font-weight: bold;
}

.stat-change {
    font-size: .75rem;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    color: #94a3b8;
}

.stat-change.positive {
    color: #27AE60;
}

/* Filters */
.search-section {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.search-section.collapsed .filter-header {
    border-radius: 12px;
    border-bottom: none;
}

.filter-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.filter-header-buttons {
    display: flex;
    gap: 10px;
    align-items: center;
}

.toggle-filter-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.toggle-filter-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: translateY(-1px);
}

.reset-filter-header-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border: none;
    color: white;
    font-size: .9rem;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
}

.reset-filter-header-btn:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-1px);
}

.reset-icon {
    font-size: 1.1rem;
    font-weight: bold;
    display: inline-block;
    transition: transform .3s;
}

.reset-filter-header-btn:hover .reset-icon {
    transform: rotate(180deg);
}

.search-controls-wrapper {
    padding: 16px 20px;
}

.search-controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.search-group {
    display: flex;
    flex-direction: column;
    position: relative;
}

.search-label {
    font-size: .875rem;
    color: #34495E;
    margin-bottom: 4px;
    font-weight: 500;
}

.search-input {
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    font-size: 14px;
    transition: all .3s;
    background: #fff;
    min-height: 44px;
}

.search-input:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 3px rgba(210, 105, 30, .15);
}

.search-input:hover {
    border-color: #D2691E;
    background: #fff8f3;
}

/* Multi-select */
.multi-select-container {
    position: relative;
}

.multi-select-display {
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all .3s;
    min-height: 44px;
}

.multi-select-display:hover {
    border-color: #D2691E;
    background: #fff8f3;
}

.selected-items {
    color: #495057;
    font-weight: 500;
}

.dropdown-arrow {
    color: #999;
    font-size: .75rem;
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 2px solid #D2691E;
    border-radius: 8px;
    margin-top: 4px;
    max-height: 250px;
    overflow-y: auto;
    z-index: 9999;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.select-all-option {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    background: #f8f9fa;
}

.dropdown-options {
    max-height: 200px;
}

.checkbox-option {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    cursor: pointer;
    transition: background .2s;
}

.checkbox-option:hover {
    background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
    margin-right: 8px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #D2691E;
}

.multi-badge {
    background: #FF8C00;
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: .75rem;
    font-weight: 600;
    margin-left: 4px;
}

.orange-badge {
    background: #FF8C00;
}

.columns-select {
    border: 2px solid #FF8C00;
    background: #FFF8F3;
}

.columns-dropdown {
    max-height: 400px;
}

.filter-actions {
    display: flex;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}

.apply-filters-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all .3s;
    font-size: .9rem;
}

.apply-filters-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(210, 105, 30, .3);
}

.reset-filters-btn {
    padding: 10px 24px;
    background: white;
    color: #34495E;
    border: 2px solid #FFE4B5;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all .3s;
    font-size: .9rem;
}

.reset-filters-btn:hover {
    background: #FFF8F3;
    border-color: #FF8C00;
    transform: translateY(-1px);
}

/* Grid */
.grid-container {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    overflow: hidden;
    border: 1px solid #cbd5e1;
}

.grid-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
}

.grid-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.report-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #334155;
}

.date-range-label {
    font-size: .9rem;
    color: #64748b;
    font-weight: 500;
    font-style: italic;
}

.record-count-badge {
    padding: 2px 10px;
    background: #FF8C00;
    color: white;
    border-radius: 20px;
    font-size: .75rem;
    font-weight: 700;
    margin-left: 8px;
}

.grid-controls {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.rows-selector {
    display: flex;
    align-items: center;
    gap: 8px;
}

.rows-label {
    font-size: .875rem;
    color: #6c757d;
    font-weight: 500;
}

.rows-select {
    padding: 6px 12px;
    border: 1px solid #94a3b8;
    border-radius: 6px;
    background: white;
    color: #475569;
    font-size: .875rem;
    cursor: pointer;
    min-width: 100px;
}

.grid-actions {
    display: flex;
    gap: 8px;
}

.action-button {
    padding: 8px 16px;
    background: white;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    color: #475569;
    font-size: .875rem;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    white-space: nowrap;
}

.action-button:hover {
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.export-btn {
    color: #10B981;
    border-color: #D1FAE5;
}

.export-btn:hover {
    background: #ECFDF5;
    border-color: #10B981;
}

.popout-btn {
    color: #8B5CF6;
    border-color: #DDD6FE;
}

.popout-btn:hover {
    background: #F3E8FF;
    border-color: #8B5CF6;
}

.refresh-btn {
    color: #3B82F6;
    border-color: #DBEAFE;
}

.refresh-btn:hover {
    background: #EFF6FF;
    border-color: #3B82F6;
}

.clear-column-searches-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .2s;
    display: flex;
    align-items: center;
    gap: 6px;
}

.clear-column-searches-btn:hover {
    transform: translateY(-1px);
}

/* Table */
.table-container {
    overflow-y: auto;
    position: relative;
    max-height: 600px;
}

.loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, .8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
}

.loader {
    width: 40px;
    height: 40px;
    border: 4px solid #e2e8f0;
    border-top-color: #D2691E;
    border-radius: 50%;
    animation: spin .7s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    z-index: 10;
}

.data-table th {
    padding: 12px 16px;
    text-align: left;
    color: white;
    font-weight: 600;
    font-size: .875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
    white-space: nowrap;
}

.sortable-th {
    cursor: pointer;
    user-select: none;
}

.sortable-th:hover {
    background: rgba(255, 255, 255, .15);
}

.sort-indicator {
    font-size: .65rem;
    margin-left: 4px;
    opacity: .7;
}

.data-table tbody tr {
    border-bottom: 1px solid #e2e8f0;
    transition: all .2s;
    background: #fff;
    cursor: pointer;
}

.data-table tbody tr:hover {
    background: #fff8f3;
}

.row-success {
    background: rgba(39, 174, 96, .03);
}

.row-success:hover {
    background: rgba(39, 174, 96, .08) !important;
}

.row-failed {
    background: rgba(255, 193, 7, .04);
}

.row-failed:hover {
    background: rgba(255, 193, 7, .09) !important;
}

.data-table td {
    padding: 12px 16px;
    font-size: .875rem;
    color: #334155;
    max-width: 300px;
    overflow: hidden;
}

.data-table td span {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.search-row {
    background: #fff3e0 !important;
}

.search-row th {
    background: transparent !important;
    padding: 6px !important;
    border-right: 1px solid #ffcc80 !important;
}

.column-search-input {
    width: 100%;
    padding: 5px 8px;
    border: 1px solid #ffcc80;
    border-radius: 4px;
    font-size: .75rem;
    background: white;
    color: #333;
    font-weight: normal;
    letter-spacing: normal;
    text-transform: none;
    outline: none;
    transition: all .2s;
}

.column-search-input:focus {
    border-color: #ff9800;
    box-shadow: 0 0 0 2px rgba(255, 152, 0, .2);
}

.column-search-input::placeholder {
    color: #999;
    font-style: italic;
}

.volume-cell-new {
    display: inline-flex;
    align-items: baseline;
    gap: 3px;
}

.volume-value {
    font-weight: 600;
    color: #1e40af;
}

.volume-unit {
    font-size: .75rem;
    color: #64748b;
    font-weight: 500;
}

.date-time-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.date-part {
    font-size: .875rem;
    color: #334155;
    font-weight: 500;
}

.id-code {
    font-family: 'Courier New', monospace;
    font-size: .75rem;
    color: #64748b;
    background: #f0f9ff;
    padding: 2px 6px;
    border-radius: 4px;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 12px;
}

.empty-title {
    font-size: 1rem;
    font-weight: 600;
    color: #475569;
}

.empty-description {
    font-size: .875rem;
    color: #94a3b8;
    margin-top: 4px;
}

/* Pagination */
.pagination-container {
    padding: 14px 20px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8fafc;
}

.pagination-info {
    font-size: .875rem;
    color: #64748b;
}

.pagination-controls {
    display: flex;
    gap: 6px;
    align-items: center;
}

.page-btn {
    padding: 6px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    font-size: .8rem;
    color: #475569;
    transition: all .2s;
    font-weight: 500;
}

.page-btn:hover:not(:disabled) {
    background: #fff8f3;
    border-color: #D2691E;
    color: #D2691E;
    transform: translateY(-1px);
}

.page-btn:disabled {
    opacity: .4;
    cursor: not-allowed;
}

.page-btn.active {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-color: #D2691E;
    font-weight: 700;
}

/* Popup */
.simple-popup {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, .5);
    z-index: 9000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.popup-content {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, .25);
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    overflow: hidden;
    border: 1px solid #cbd5e1;
}

.wide-popup {
    width: 900px;
    max-width: 95vw;
}

.popup-header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.popup-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.export-pdf-btn {
    background: rgba(255, 255, 255, .2);
    border: 1px solid rgba(255, 255, 255, .3);
    color: white;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: .875rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all .2s;
    white-space: nowrap;
}

.export-pdf-btn:hover {
    background: rgba(255, 255, 255, .3);
    border-color: rgba(255, 255, 255, .5);
    transform: translateY(-1px);
}

.export-pdf-btn:active {
    transform: translateY(0);
}

.popup-header h3 {
    color: white;
    font-size: 1rem;
    font-weight: 600;
}

.close-btn {
    background: rgba(255, 255, 255, .2);
    border: none;
    color: white;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .2s;
}

.close-btn:hover {
    background: rgba(255, 255, 255, .4);
}

.popup-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
}

.detail-sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.detail-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 14px;
    border: 1px solid #e2e8f0;
}

.detail-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-row {
    font-size: .875rem;
    color: #334155;
    padding: 4px 0;
    border-bottom: 1px solid #f1f5f9;
}

.detail-row strong {
    color: #1e293b;
    margin-right: 4px;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

@media (max-width: 768px) {
    .main-content {
        padding: 12px;
    }

    .stats-container {
        grid-template-columns: 1fr 1fr;
    }

    .search-controls {
        grid-template-columns: 1fr;
    }

    .detail-sections {
        grid-template-columns: 1fr;
    }

    .data-table {
        font-size: .75rem;
    }

    .data-table th,
    .data-table td {
        padding: 8px;
    }
}

@media print {

    .header-toolbar,
    .search-section,
    .stats-section,
    .pagination-container,
    .simple-popup {
        display: none !important;
    }

    .grid-container {
        box-shadow: none;
        border: none;
    }

    .table-container {
        max-height: none !important;
        overflow: visible !important;
    }

    .data-table thead {
        background: #ccc !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
}

.date-filter-group {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 8px 6px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.date-range-showing {
  font-size: 0.75rem;
  font-weight: 400;
  color: #64748b;
  text-transform: none;
  letter-spacing: 0;
  display: inline-block;
  margin-left: 6px;
  font-weight: normal;
}

.date-quick-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.date-quick-btn {
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  background: white;
  border-radius: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.date-quick-btn:hover {
  border-color: #D2691E;
  color: #D2691E;
  background: #fff9f5;
}

.date-quick-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
  box-shadow: 0 2px 8px rgba(210, 105, 30, 0.2);
}

.specific-date-input {
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  font-size: 0.85rem;
  color: #334155;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
  width: 100%;
  max-width: 200px;
}

.specific-date-input:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 2px rgba(210, 105, 30, 0.15);
}
</style>